import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Input, Select, Button, Space, Tag, Row, Col, Statistic, Progress, message, Modal } from 'antd';
import { SearchOutlined, ReloadOutlined, SyncOutlined } from '@ant-design/icons';
import ReactECharts from 'echarts-for-react';
import { yuan } from '../utils/format.js';
import { apiFetch, onDataChanged } from '../utils/api.js';

export default function Logistics() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ keyword: '', signStatus: 'all', province: 'all' });
  const [filterOptions, setFilterOptions] = useState({ provinces: [], signStatuses: [] });
  const [stats, setStats] = useState({ signStats: [], provinceStats: [] });
  const [syncing, setSyncing] = useState(false);

  // 同步物流轨迹（快递100）
  const syncLogistics = () => {
    Modal.confirm({
      title: '同步物流轨迹',
      content: '将根据订单的单号，调快递100查询未签收订单的最新物流轨迹和签收状态（约每 100 单 1 分钟），是否继续？',
      okText: '开始同步',
      cancelText: '取消',
      onOk: async () => {
        setSyncing(true);
        message.loading({ content: '正在同步物流轨迹，请稍候…', key: 'sync', duration: 0 });
        try {
          const res = await apiFetch('/api/logistics/sync', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ onlyUnSigned: true, limit: 500 }),
          }).then(r => r.json());
          if (res.success) {
            const quotaTip = res.quotaExceeded > 0 ? `；⚠️ ${res.quotaExceeded} 单因「快递100额度用尽」未查到，请充值后再次同步` : '';
            message.success({ content: `同步完成：成功 ${res.ok} 单，暂无轨迹 ${res.skipped}，无法识别 ${res.noCom}，失败 ${res.failed}${quotaTip}`, key: 'sync', duration: 8 });
          } else {
            message.error({ content: res.error || '同步失败', key: 'sync' });
          }
        } catch (e) {
          message.error({ content: '同步失败：' + (e.message || e), key: 'sync' });
        }
        setSyncing(false);
        loadData();
        loadFilters();
      },
    });
  };

  const loadFilters = useCallback(() => {
    apiFetch('/api/filters').then(r => r.json()).then(d => {
      setFilterOptions({ provinces: d.provinces, signStatuses: d.signStatuses });
    });
    apiFetch('/api/logistics/stats').then(r => r.json()).then(setStats);
  }, []);

  const loadData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, pageSize, ...filters });
    apiFetch(`/api/logistics?${params}`)
      .then(r => r.json())
      .then(d => { setData(d.data); setTotal(d.total); setLoading(false); })
      .catch(() => setLoading(false));
  }, [page, pageSize, filters]);

  useEffect(() => { loadFilters(); }, [loadFilters]);
  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => onDataChanged(() => { loadData(); }), [loadData]); // 数据变更静默刷新

  const handleSearch = () => { setPage(1); loadData(); };
  const handleReset = () => { setFilters({ keyword: '', signStatus: 'all', province: 'all' }); setPage(1); };

  const columns = [
    { title: '运单编号', dataIndex: 'tracking_no', width: 160, fixed: 'left' },
    { title: '平台订单号', dataIndex: 'platform_order_no', width: 200, ellipsis: true,
      render: v => v ? <span style={{ color: '#D85A30', fontWeight: 500 }}>{v}</span> : <span style={{ color: '#bfbfbf' }}>—</span> },
    { title: '寄件时间', dataIndex: 'send_time', width: 150 },
    { title: '寄件网点', dataIndex: 'sender_branch', width: 130 },
    { title: '始发地', dataIndex: 'origin', width: 90 },
    { title: '派件网点', dataIndex: 'receiver_branch', width: 150 },
    { title: '目的地', dataIndex: 'destination', width: 100 },
    { title: '收件省份', dataIndex: 'receiver_province', width: 100 },
    { title: '收件人', dataIndex: 'receiver_name', width: 70 },
    { title: '包裹尺寸（厘米）', width: 130,
      render: (_, r) => `${r.package_length}×${r.package_width}×${r.package_height}` },
    { title: '重量（公斤）', dataIndex: 'weight', width: 80, align: 'right' },
    { title: '运费', dataIndex: 'shipping_fee', width: 80, align: 'right', render: v => `¥${yuan(v)}` },
    { title: '签收状态', dataIndex: 'sign_status', width: 110,
      render: v => {
        const color = v === '正常签收' ? '#3F8600' : v === '退件签收' ? '#CF1322' : v === '未签收' ? '#EF9F27' : '#378ADD';
        return <Tag color={color}>{v}</Tag>;
      } },
    { title: '最新轨迹', dataIndex: 'latest_trace', width: 260, ellipsis: true,
      render: (v, r) => v ? <span title={v}>{r.latest_time ? `${r.latest_time} ${v}` : v}</span> : <span style={{ color: '#bfbfbf' }}>—</span> },
  ];

  // 签收状态饼图
  const signChartOption = {
    tooltip: { trigger: 'item' },
    legend: { bottom: 0, type: 'scroll' },
    series: [{
      type: 'pie', radius: ['35%', '65%'],
      label: { formatter: '{b}\n{c}单 ({d}%)' },
      data: stats.signStats.map(s => ({ name: s.sign_status, value: s.count })),
    }],
  };

  // 省份物流量柱状图
  const provinceChartOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: stats.provinceStats.map(p => p.province), axisLabel: { rotate: 45, fontSize: 11 } },
    yAxis: { type: 'value', name: '票数' },
    series: [{ name: '物流票数', type: 'bar', itemStyle: { color: '#D85A30' }, data: stats.provinceStats.map(p => p.count) }],
  };

  const totalSign = stats.signStats.reduce((a, b) => a + b.count, 0);
  const signedCount = stats.signStats.filter(s => s.sign_status.includes('正常签收')).reduce((a, b) => a + b.count, 0);

  return (
    <div className="page-container">
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#D85A30', padding: '16px 18px' }}>
            <div className="stat-label">物流总票数</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{total}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>票</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#3F8600', padding: '16px 18px' }}>
            <div className="stat-label">已签收</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#3F8600' }}>{signedCount}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>票</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#EF9F27', padding: '16px 18px' }}>
            <div className="stat-label">签收率</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#26201C' }}>{totalSign > 0 ? ((signedCount / totalSign) * 100).toFixed(1) : 0}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>%</span></div>
          </div>
        </Col>
        <Col xs={12} md={6}>
          <div className="stat-card" style={{ '--sc': '#CF1322', padding: '16px 18px' }}>
            <div className="stat-label">异常(退件)</div>
            <div className="stat-value" style={{ fontSize: 22, color: '#CF1322' }}>{stats.signStats.filter(s => s.sign_status === '退件签收').reduce((a, b) => a + b.count, 0)}<span style={{ fontSize: 12, color: '#8C8178', marginLeft: 4 }}>票</span></div>
          </div>
        </Col>
      </Row>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap size="middle">
          <Input.Search
            placeholder="搜索运单号/客户/目的地"
            allowClear
            style={{ width: 280 }}
            value={filters.keyword}
            onChange={e => setFilters({ ...filters, keyword: e.target.value })}
            onSearch={handleSearch}
          />
          <Select style={{ width: 130 }} value={filters.signStatus} onChange={v => setFilters({ ...filters, signStatus: v })}
            options={[{ value: 'all', label: '全部签收状态' }, ...filterOptions.signStatuses.map(s => ({ value: s, label: s }))]} />
          <Select style={{ width: 130 }} value={filters.province} onChange={v => setFilters({ ...filters, province: v })}
            showSearch optionFilterProp="label"
            options={[{ value: 'all', label: '全部省份' }, ...filterOptions.provinces.map(p => ({ value: p, label: p }))]} />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>查询</Button>
          <Button icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
          <Button type="primary" danger icon={<SyncOutlined />} loading={syncing} onClick={syncLogistics}>同步物流</Button>
        </Space>
      </Card>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} lg={10}>
          <Card title="签收状态分布"><ReactECharts option={signChartOption} theme="brand" style={{ height: 280 }} /></Card>
        </Col>
        <Col xs={24} lg={14}>
          <Card title="各省份物流票数"><ReactECharts option={provinceChartOption} theme="brand" style={{ height: 280 }} /></Card>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        scroll={{ x: 1500 }}
        size="small"
        pagination={{
          current: page, pageSize, total,
          showSizeChanger: true, showQuickJumper: true,
          showTotal: t => `共 ${t} 条`,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
      />
    </div>
  );
}
